import { createShipIconNL } from "@components/map/mapComponents/shipIcon"
import { updateSidePanelState } from "@redux/features/mapSidePanelControl/mapSidePanelControlSlice"
import { setSelectedVessel } from "@redux/features/selectedVessel/selectedVesselSlice"
import type { LatLng } from "leaflet"
import { DataSetter } from "../utils/mapLayers"
import { getVesselDisplayName } from "../utils/vesselMetadata"
import { getWorldCopyLongitudeOffsets, getWorldCopyPoints } from "./vesselTrackMapEdges"

export const addVesselsNL: DataSetter = (
	dispatch,
	queryClient,
	layerGroup,
	layerData,
	navigate,
	map
) => {
	if (layerGroup === undefined) return

	const { vesselsNlData } = layerData
	if (vesselsNlData === undefined) return

	const { company, vesselInfo, selectedVessel } = vesselsNlData
	if (vesselInfo.length === 0) return

	layerGroup.clearLayers()
	const longitudeOffsets = getWorldCopyLongitudeOffsets(map?.getBounds())

	for (const ship of vesselInfo) {
		if (
			ship?.position?.hasOwnProperty("lat") !== true ||
			ship?.position?.hasOwnProperty("lon") !== true
		) {
			continue
		}
		if (isNaN(ship.position.lat) === true || isNaN(ship.position.lon) == true) {
			continue
		}
		const shipCoords: LatLng[] = getWorldCopyPoints(
			{
				latitude: ship.position.lat,
				longitude: ship.position.lon,
			},
			longitudeOffsets
		).map((point) => L.latLng(point.latitude, point.longitude))

		const vesselDisplayName = getVesselDisplayName(ship, company)

		const isSelected =
			selectedVessel?.toLowerCase() === ship.name.toLowerCase() ||
			selectedVessel?.toLowerCase() === vesselDisplayName.toLowerCase()
		const plantNameNormalized = vesselDisplayName.replaceAll(" ", "-")

		const labelIcon = L.divIcon({
			className: `div-vessel-${plantNameNormalized} text-white overflow-visible`,
			html: `<span data-vessel-label-selected="${isSelected}" class="vessel-label vessel-${plantNameNormalized} drop-shadow-[1px_1px_0.5px_rgba(0,0,0,0.35),1px_-0.5px_0.5px_rgba(0,0,0,0.35),-0.5px_1px_0.5px_rgba(0,0,0,0.35),-0.5px_-0.5px_0.5px_rgba(0,0,0,0.35)] pl-1 w-max pr-1 py-0 font-bold block rounded-sm">${
				vesselDisplayName
			}</span>`,
			iconSize: [1, 20],
			iconAnchor: [-15, 10],
		})

		for (const shipCoord of shipCoords) {
			const marker = L.marker(shipCoord, {
				icon: createShipIconNL(
					ship.position.heading,
					ship.condition,
					isSelected,
					ship.position.lat,
					ship.lengthOA,
					ship.breadthMld,
					map?.getZoom()
				),
				pane: "vesselsPane",
			}).on("click", () => {
				if (ship === null) {
					return
				}
				dispatch(setSelectedVessel({ ...ship, name: vesselDisplayName }))
				dispatch(
					updateSidePanelState({
						isTableExpanded: false,
						isOpen: true,
						selectedTab: "1",
					})
				)
				if (navigate !== undefined) {
					navigate({
						to: `.`,
						search: (prev) => ({
							...prev,
							vessel: vesselDisplayName,
						}),
					})
				}
			})

			const labelOffset = L.latLng(shipCoord.lat, shipCoord.lng)

			const labelMarker = L.marker(labelOffset, {
				icon: labelIcon,
				interactive: false,
				pane: "tooltipPane",
			})

			const getLabelElements = () => {
				const labelDiv = labelMarker.getElement()
				return {
					labelDiv,
					label: labelDiv?.querySelector<HTMLElement>(".vessel-label") ?? null,
				}
			}

			layerGroup.addLayer(marker)
			layerGroup.addLayer(labelMarker)

			marker.on("mouseover", () => {
				if (isSelected === true) {
					return
				}
				const markerContainer = marker.getElement()
				if (markerContainer !== undefined) {
					markerContainer.style.setProperty("border-radius", "99999px", "important")
					markerContainer.style.setProperty("border", "2px solid #ffffffff", "important")
					markerContainer.style.setProperty("background-color", "#ffffff44", "important")
					markerContainer.style.setProperty("z-index", "10000000", "important")
				}
				const { labelDiv, label } = getLabelElements()
				if (labelDiv !== undefined) {
					labelDiv.style.setProperty("z-index", "10000001", "important")
				}

				if (label !== null) {
					label.dataset.hoveredFromHidden =
						label.style.display === "none" ? "true" : "false"
					label.style.setProperty("display", "block")
					label.style.setProperty("background-color", "rgba(0,0,0,0.35)", "important")
					label.style.setProperty("position", "relative", "important")
					label.style.setProperty("z-index", "10000001", "important")
				}
			})
			marker.on("mouseout", () => {
				if (isSelected === true) {
					return
				}
				const markerContainer = marker.getElement()
				if (markerContainer !== undefined) {
					markerContainer.style.setProperty("border", "2px solid #ffffff00", "important")
					markerContainer.style.setProperty("background-color", "#ffffff00", "important")
					markerContainer.style.setProperty("z-index", "1", "important")
				}

				const { labelDiv, label } = getLabelElements()
				if (labelDiv !== undefined) {
					labelDiv.style.setProperty("z-index", "1", "important")
				}

				if (label !== null) {
					if (label.dataset.hoveredFromHidden === "true") {
						label.style.setProperty("display", "none")
					} else {
						label.style.removeProperty("display")
					}
					delete label.dataset.hoveredFromHidden
					label.style.setProperty("background-color", "#00000000")
					label.style.setProperty("position", "relative", "important")
					label.style.setProperty("z-index", "1", "important")
				}
			})

			if (isSelected === true) {
				const { label } = getLabelElements()
				if (label !== null) {
					label.style.setProperty("display", "block")
					label.style.setProperty("background-color", "rgba(0,0,0,0.35)", "important")
					label.style.setProperty("position", "relative", "important")
					label.style.setProperty("z-index", "10000000", "important")
				}
			}
		}
	}
}
